<template>
    <div class="bg-blue-light p-1">
        <TitleButton
            btnTitle="Create Quotation"
            :showBtn="false"
            :showAddNew="true"
            @onClickAddNewButton="onClickAddNewButton"
        />
    </div>

    <div class="my-2 px-2">
        <DateQuerySetter
            btnTitle="Go"
            @onClickGo="onClickGo"
        >
            <div class="col-12 col-sm-6 col-md-4 col-lg-3 mr-top-1">
                <label class="form-label">Customer Name</label>
                <input
                    class="form-control"
                    placeholder="Customer Name"
                    :value="dealCustomer.name"
                    disabled
                />
            </div>

            <div class="col-12 col-sm-6 col-md-4 col-lg-3 mr-top-1">
                <label class="form-label">Status</label>
                <v-select
                    placeholder="Select Status"
                    v-model="status"
                    :options="statusList"
                    label="name"
                    :reduce="option => option.value"
                />
            </div>
        </DateQuerySetter>
    </div>
    
    <div class="col-12 px-2 mt-2">
        <ListTable 
            :quotation-list="quotationList"
            :dealId="dealId"
        />
        <div class="px-2 bottom-0 mt-2">
            <Pagination
                :offset="offset"
                @onPageChange="onPageChange"
                ref="pagination"
            />
        </div>
    </div>

    <Loader v-if="loading"/>
</template>

<script setup>
import { ref, onMounted, computed, inject } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from "vuex";
import Loader from "@/components/atom/LoaderComponent.vue";
import TitleButton from '@/components/atom/TitleButton.vue';
import AsyncSelect from "@/components/molecule/input-field/AsyncSelect.vue";
import DateQuerySetter from '@/components/atom/DateQuerySetter.vue';
import Pagination from '@/components/atom/Pagination';
import ListTable from '@/components/molecule/company/admin/open-deal/List.vue';
import handleAdmin from '@/services/modules/admin/admin';
import handlePurchase from '@/services/modules/purchase';

const { fetchQuotationList } = handleAdmin();
const { fetchOrderStatus } = handlePurchase();

const props = defineProps({
    dealCustomer: Object,
    dealId: Number,
})

const route = useRoute();
const router = useRouter();
const store = useStore();
const showError = inject('showError');
const showSuccess = inject('showSuccess');
const loading = ref(false);

const start = computed(() => route.query.start);
const end = computed(() => route.query.end);
const page = computed(() => route.query.page);
const companyId = computed(() => route.params.companyId);

const quotationList = ref([]);
const offset = ref(10);
const status = ref('pending');
const statusList = ref([]);

const onClickGo = () => {
    getQuotationList();
}

const onPageChange = async (page) => {
    let routeQuery = { ...route.query }
    routeQuery.page = page
    await router.push({path: route.path, query: routeQuery})
    await getQuotationList()
}

const setPaginationData = (data) => {
    store.commit('setPaginationData', data)
}

const setPagination = (data) => {
    setPaginationData({
      records: data.total,
      perPage: data.per_page,
      currentPage: data.current_page,
    })
}

const getQuotationList = async () => {
    loading.value = true;
    const query = getQuery();
     try {
        const res = await fetchQuotationList(query);
        if (!res.status) {
            showError(res.message);
            return store.commit("resetPagination")
        }
        quotationList.value = res.data.data;
        if(quotationList.value.length > 0) {
            setPagination(res.data)
        }
    } catch (err) {
        console.error(err);
    } finally {
        loading.value = false;
    }
};

const getOrderStatus = async () => {
    const res = await fetchOrderStatus('?company_id=' + companyId.value);
    if (res.status) {
        statusList.value = res.data.filter(item => ['pending', 'approved'].includes(item.value))
    }
}

const getQuery = () => {
    let query = '?company_id=' + companyId.value
    query += '&offset=' + offset.value
    if (start) query += '&start_date=' + start.value
    if (end) query += '&end_date=' + end.value
    if(props.dealId) query += '&deal_id=' + props.dealId
    if(status.value) query += '&status=' + status.value
    if (page.value) query += '&page=' + page.value

    return query
};

const onClickAddNewButton = () => {
    router.push({
      name: 'deal-create-quotation',
      params: route.params,
      query: {
        ...route.query,
        dealId: props.dealId,
      }
    })
}

onMounted( () => {
    if(props.dealId) {
        getQuotationList();
        getOrderStatus();
    }
})
</script>

<style scoped>
.mr-top-1 {
    margin-top: 1% !important;
}
</style>